"use client";

import React from "react";
import { useQuery } from "@tanstack/react-query";
import { Card, CardContent } from "@/components/ui/card";
import { ActivityFeed, type ActivityItem } from "@/components/shared/activity-feed";
import { activityApi } from "@/lib/api/services/activity.api";
import { EmptyState } from "@/components/shared/empty-state";
import { Clock, MessageSquare } from "lucide-react";

export default function FeedPage() {
  const { data: activities, isLoading } = useQuery({
    queryKey: ["feed"],
    queryFn: async () => {
      const data = await activityApi.listMine();
      const items = ((data as { data?: Array<Record<string, unknown>> }).data ?? []);
      return items.map((item: Record<string, unknown>, idx: number) => ({
        id: String(item.id ?? idx),
        type: ((item.action as string) || "notification") as ActivityItem["type"],
        title: (item.title as string) || (item.action as string) || "",
        description: (item.description as string) || (item.entityType as string) || "",
        timestamp: item.createdAt ? new Date(item.createdAt as string).toLocaleString() : "",
        icon: MessageSquare,
        color: "text-muted-foreground",
      })) as ActivityItem[];
    },
  });

  return (
    <div className="mx-auto max-w-3xl px-4 py-8">
      <div className="mb-6 space-y-1">
        <h1 className="font-heading text-xl font-semibold tracking-tight text-foreground">Activity</h1>
        <p className="text-sm text-muted-foreground leading-relaxed">Recent updates from your network and the platform.</p>
      </div>
      <Card>
        <CardContent className="p-0">
          {isLoading ? (
            <div className="space-y-4 p-4">
              {[1, 2, 3].map((i) => (
                <div key={i} className="flex items-start gap-3">
                  <div className="h-8 w-8 rounded-full bg-muted/10 animate-pulse shrink-0" />
                  <div className="flex-1 space-y-2">
                    <div className="h-4 w-3/4 bg-muted/10 rounded animate-pulse" />
                    <div className="h-3 w-1/2 bg-muted/10 rounded animate-pulse" />
                  </div>
                </div>
              ))}
            </div>
          ) : !activities || activities.length === 0 ? (
            <EmptyState title="No recent activity" description="Your activity feed will appear here as you interact with the platform." icon={<Clock className="h-8 w-8 text-muted-foreground/40" />} />
          ) : (
            <ActivityFeed activities={activities} />
          )}
        </CardContent>
      </Card>
    </div>
  );
}
